import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, TrendingDown, TrendingUp, Wallet, Home, KeyRound, Calendar, MapPin, Target, PhoneCall, Mail, ArrowRight, CheckCircle2 } from 'lucide-react';
import { formatCurrency } from '../lib/utils';
import type { Scenario } from './ScenarioSummary';
interface PersonalReportProps {
  scenario: Scenario;
  estimatedPayment?: number;
  currentPayment?: number;
  monthlySavings?: number;
  accessibleEquity?: number;
}
export function PersonalReport({
  scenario,
  estimatedPayment,
  currentPayment,
  monthlySavings,
  accessibleEquity
}: PersonalReportProps) {
  const firstName = scenario.name?.split(' ')[0] || 'there';
  const intentTitle = scenario.intent === 'purchase' ? 'Affordability Report' : scenario.intent === 'refinance' ? 'Refinance Report' : 'Home Equity Report';
  return <div className="bg-white rounded-3xl shadow-xl border border-forest/10 overflow-hidden flex flex-col" data-id="element-106">
      {/* Header */}
      <div className="bg-forest-600 p-6 sm:p-7 text-white relative overflow-hidden" data-id="element-107">
        <div className="absolute top-0 right-0 w-48 h-48 bg-lime/15 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3 pointer-events-none" data-id="element-108" />
        <div className="relative z-10" data-id="element-109">
          <div className="flex items-center gap-2 mb-2" data-id="element-110">
            <Sparkles className="w-4 h-4 text-lime-300" data-id="element-111" />
            <span className="text-xs uppercase tracking-wider font-bold text-lime-300" data-id="element-112">
              Personalized Report
            </span>
          </div>
          <h2 className="text-2xl font-extrabold leading-tight" data-id="element-113">
            Hey {firstName}, here's your{' '}
            <span className="text-lime-300 lowercase" data-id="element-114">
              {intentTitle.toLowerCase()}
            </span>
          </h2>
          <p className="text-sm text-white/70 mt-2" data-id="element-115">
            A snapshot of your scenario, built from your answers.
          </p>
        </div>
      </div>

      <div className="p-6 sm:p-7 space-y-6 bg-cream/30" data-id="element-116">
        {/* HEADLINE numbers — intent-specific */}
        {scenario.intent === 'equity' && <EquityHeadline accessibleEquity={accessibleEquity} estimatedPayment={estimatedPayment} cashOutAmount={scenario.cashOutAmount} data-id="element-117" />}

        {scenario.intent === 'refinance' && <RefinanceHeadline estimatedPayment={estimatedPayment} currentPayment={currentPayment} monthlySavings={monthlySavings} data-id="element-118" />}

        {scenario.intent === 'purchase' && <PurchaseHeadline estimatedPayment={estimatedPayment} budget={scenario.budget} downPayment={scenario.downPayment} data-id="element-119" />}

        {/* DETAILS — borrower's inputs grouped nicely */}
        <DetailsSection scenario={scenario} data-id="element-120" />

        {/* NEXT STEPS */}
        <NextStepsCard scenario={scenario} firstName={firstName} data-id="element-121" />
      </div>
    </div>;
}
/* ---------- Headline blocks per intent ---------- */
function EquityHeadline({
  accessibleEquity,
  estimatedPayment,
  cashOutAmount
}: {
  accessibleEquity?: number;
  estimatedPayment?: number;
  cashOutAmount?: number;
}) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-122">
      <HeroTile eyebrow="Accessible home equity" value={accessibleEquity != null && accessibleEquity > 0 ? formatCurrency(accessibleEquity) : '—'} sub="That's how much cash you could tap into." accent="lime" icon={<Wallet className="w-4 h-4" data-id="element-124" />} data-id="element-123" />
      <HeroTile eyebrow="Approx equity payment" value={estimatedPayment != null && estimatedPayment > 0 ? `${formatCurrency(estimatedPayment)}/mo` : '—'} sub={cashOutAmount ? `Interest-only @ 7% on ${formatCurrency(cashOutAmount)}.` : 'Interest-only @ 7% APR.'} accent="forest" icon={<TrendingUp className="w-4 h-4" data-id="element-126" />} data-id="element-125" />
    </div>;
}
function RefinanceHeadline({
  estimatedPayment,
  currentPayment,
  monthlySavings
}: {
  estimatedPayment?: number;
  currentPayment?: number;
  monthlySavings?: number;
}) {
  const savesMoney = monthlySavings != null && monthlySavings > 0;
  return <div className="space-y-3" data-id="element-127">
      {monthlySavings != null && monthlySavings !== 0 && <HeroTile eyebrow={savesMoney ? 'Estimated monthly savings' : 'Monthly difference'} value={`${formatCurrency(Math.abs(monthlySavings))}/mo`} sub={savesMoney ? `That's roughly ${formatCurrency(monthlySavings * 12)} a year staying in your pocket.` : 'Your new payment would be slightly higher — an advisor can help find a better option.'} accent={savesMoney ? 'coral' : 'forest'} icon={savesMoney ? <TrendingDown className="w-4 h-4" data-id="element-129" /> : <TrendingUp className="w-4 h-4" data-id="element-130" />} data-id="element-128" />}
      <div className="grid grid-cols-2 gap-3" data-id="element-131">
        <SmallStat label="New payment" value={estimatedPayment != null && estimatedPayment > 0 ? `${formatCurrency(estimatedPayment)}/mo` : '—'} tone="lime" data-id="element-132" />
        <SmallStat label="Current payment" value={currentPayment != null && currentPayment > 0 ? `${formatCurrency(currentPayment)}/mo` : '—'} tone="muted" data-id="element-133" />
      </div>
    </div>;
}
function PurchaseHeadline({
  estimatedPayment,
  budget,
  downPayment
}: {
  estimatedPayment?: number;
  budget?: number;
  downPayment?: number;
}) {
  const loanAmount = budget && downPayment != null ? Math.max(0, budget - downPayment) : undefined;
  return <div className="space-y-3" data-id="element-134">
      <HeroTile eyebrow="Estimated monthly payment" value={estimatedPayment != null && estimatedPayment > 0 ? `${formatCurrency(estimatedPayment)}/mo` : '—'} sub="Principal & interest only. Taxes & insurance vary by area." accent="lime" icon={<KeyRound className="w-4 h-4" data-id="element-136" />} data-id="element-135" />
      <div className="grid grid-cols-2 gap-3" data-id="element-137">
        <SmallStat label="Loan amount" value={loanAmount != null ? formatCurrency(loanAmount) : '—'} tone="forest" data-id="element-138" />
        <SmallStat label="Down payment" value={downPayment != null ? formatCurrency(downPayment) : '—'} tone="muted" data-id="element-139" />
      </div>
    </div>;
}
/* ---------- Details (collected inputs) ---------- */
function DetailsSection({
  scenario
}: {
  scenario: Scenario;
}) {
  const rows: {
    icon: React.ReactNode;
    label: string;
    value: string;
  }[] = [];
  // Rates (purchase + refi)
  if (scenario.currentRate) rows.push({
    icon: <Target className="w-3.5 h-3.5" data-id="element-140" />,
    label: 'Current rate',
    value: `${scenario.currentRate}%`
  });
  if (scenario.targetRate) rows.push({
    icon: <Target className="w-3.5 h-3.5" data-id="element-141" />,
    label: 'Target rate',
    value: `${scenario.targetRate}%`
  });
  // Intent-specific
  if (scenario.intent === 'purchase') {
    if (scenario.budget) rows.push({
      icon: <Home className="w-3.5 h-3.5" data-id="element-142" />,
      label: 'Target price',
      value: formatCurrency(scenario.budget)
    });
    if (scenario.loanTerm) rows.push({
      icon: <Calendar className="w-3.5 h-3.5" data-id="element-143" />,
      label: 'Loan term',
      value: `${scenario.loanTerm} years`
    });
    if (scenario.underContract) rows.push({
      icon: <CheckCircle2 className="w-3.5 h-3.5" data-id="element-144" />,
      label: 'Under contract',
      value: scenario.underContract
    });
    if (scenario.agent) rows.push({
      icon: <CheckCircle2 className="w-3.5 h-3.5" data-id="element-145" />,
      label: 'Has agent',
      value: scenario.agent
    });
  } else if (scenario.intent === 'refinance') {
    if (scenario.homeValue) rows.push({
      icon: <Home className="w-3.5 h-3.5" data-id="element-146" />,
      label: 'Home value',
      value: formatCurrency(scenario.homeValue)
    });
    if (scenario.currentBalance) rows.push({
      icon: <Wallet className="w-3.5 h-3.5" data-id="element-147" />,
      label: 'Loan balance',
      value: formatCurrency(scenario.currentBalance)
    });
    if (scenario.loanTerm) rows.push({
      icon: <Calendar className="w-3.5 h-3.5" data-id="element-148" />,
      label: 'New loan term',
      value: `${scenario.loanTerm} years`
    });
    if (scenario.goal) rows.push({
      icon: <Target className="w-3.5 h-3.5" data-id="element-149" />,
      label: 'Goal',
      value: scenario.goal
    });
  } else {
    if (scenario.homeValue) rows.push({
      icon: <Home className="w-3.5 h-3.5" data-id="element-150" />,
      label: 'Home value',
      value: formatCurrency(scenario.homeValue)
    });
    if (scenario.currentBalance) rows.push({
      icon: <Wallet className="w-3.5 h-3.5" data-id="element-151" />,
      label: 'Mortgage balance',
      value: formatCurrency(scenario.currentBalance)
    });
    if (scenario.cashOutAmount) rows.push({
      icon: <Wallet className="w-3.5 h-3.5" data-id="element-152" />,
      label: 'Cash needed',
      value: formatCurrency(scenario.cashOutAmount)
    });
    if (scenario.goal) rows.push({
      icon: <Target className="w-3.5 h-3.5" data-id="element-153" />,
      label: 'Using it for',
      value: scenario.goal
    });
  }
  if (scenario.propertyState) rows.push({
    icon: <MapPin className="w-3.5 h-3.5" data-id="element-154" />,
    label: 'Property state',
    value: scenario.propertyState
  });
  if (scenario.timeline) rows.push({
    icon: <Calendar className="w-3.5 h-3.5" data-id="element-155" />,
    label: 'Timeline',
    value: scenario.timeline
  });
  if (rows.length === 0) {
    return <div className="text-center py-6 text-forest/50 text-sm bg-white rounded-2xl border border-forest/5" data-id="element-156">
        Your report will fill in as you answer questions.
      </div>;
  }
  return <div className="bg-white rounded-2xl border border-forest/10 p-5" data-id="element-157">
      <h3 className="text-xs uppercase tracking-wider font-bold text-forest/60 mb-3" data-id="element-158">
        Your details
      </h3>
      <div className="grid grid-cols-1 gap-2.5" data-id="element-159">
        {rows.map((row, idx) => <motion.div key={row.label} initial={{
        opacity: 0,
        x: -6
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: idx * 0.03
      }} className="flex items-center justify-between text-sm border-b border-forest/5 pb-2 last:border-b-0 last:pb-0" data-id="element-160">
            <div className="flex items-center gap-2 text-forest/70" data-id="element-161">
              <span className="text-forest/40" data-id="element-162">{row.icon}</span>
              {row.label}
            </div>
            <span className="font-bold text-forest" data-id="element-163">{row.value}</span>
          </motion.div>)}
      </div>
    </div>;
}
/* ---------- Next Steps ---------- */
function NextStepsCard({
  scenario,
  firstName
}: {
  scenario: Scenario;
  firstName: string;
}) {
  return <div className="bg-forest-600 text-white rounded-2xl p-5" data-id="element-164">
      <div className="flex items-center gap-2 mb-2" data-id="element-165">
        <span className="w-7 h-7 rounded-full bg-lime/20 text-lime-300 flex items-center justify-center text-xs font-extrabold" data-id="element-166">
          1
        </span>
        <h3 className="text-base font-extrabold" data-id="element-167">Next steps, {firstName}</h3>
      </div>
      <p className="text-sm text-white/70 mb-4 leading-relaxed" data-id="element-168">
        A Loan Pronto advisor will reach out shortly to walk through your real
        options and lock in your actual rate.
      </p>

      <ul className="space-y-2 text-sm mb-5" data-id="element-169">
        {scenario.email && <li className="flex items-center gap-2 text-white/90" data-id="element-170">
            <Mail className="w-3.5 h-3.5 text-lime-300" data-id="element-171" />
            <span className="text-white/60" data-id="element-172">Sending to</span>
            <span className="font-bold" data-id="element-173">{scenario.email}</span>
          </li>}
        {scenario.phone && <li className="flex items-center gap-2 text-white/90" data-id="element-174">
            <PhoneCall className="w-3.5 h-3.5 text-lime-300" data-id="element-175" />
            <span className="text-white/60" data-id="element-176">Texting</span>
            <span className="font-bold" data-id="element-177">{scenario.phone}</span>
          </li>}
      </ul>

      <button className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-coral hover:bg-coral-500 text-white text-sm font-extrabold uppercase tracking-wider rounded-full transition-colors shadow-md" data-id="element-178">
        Talk to an advisor
        <ArrowRight className="w-4 h-4" data-id="element-179" />
      </button>
    </div>;
}
/* ---------- Atoms ---------- */
interface HeroTileProps {
  eyebrow: string;
  value: string;
  sub: string;
  accent: 'lime' | 'coral' | 'forest';
  icon: React.ReactNode;
}
function HeroTile({
  eyebrow,
  value,
  sub,
  accent,
  icon
}: HeroTileProps) {
  const styles = {
    lime: {
      bg: 'bg-lime-50 border-lime-300',
      eyebrow: 'text-lime-700',
      value: 'text-forest',
      icon: 'bg-lime-500 text-forest'
    },
    coral: {
      bg: 'bg-coral-50 border-coral-300',
      eyebrow: 'text-coral-600',
      value: 'text-forest',
      icon: 'bg-coral text-white'
    },
    forest: {
      bg: 'bg-forest-50 border-forest-200',
      eyebrow: 'text-forest-500',
      value: 'text-forest',
      icon: 'bg-forest text-white'
    }
  }[accent];
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} className={`rounded-2xl p-4 border-2 ${styles.bg}`} data-id="element-180">
      <div className="flex items-center gap-2 mb-2" data-id="element-181">
        <span className={`w-6 h-6 rounded-full flex items-center justify-center ${styles.icon}`} data-id="element-182">
          {icon}
        </span>
        <p className={`text-[11px] uppercase tracking-wider font-bold ${styles.eyebrow}`} data-id="element-183">
          {eyebrow}
        </p>
      </div>
      <div className={`text-2xl font-extrabold tracking-tight leading-none ${styles.value}`} data-id="element-184">
        {value}
      </div>
      <p className="text-xs text-forest/60 mt-2 leading-relaxed" data-id="element-185">{sub}</p>
    </motion.div>;
}
interface SmallStatProps {
  label: string;
  value: string;
  tone: 'lime' | 'forest' | 'muted';
}
function SmallStat({
  label,
  value,
  tone
}: SmallStatProps) {
  const styles = {
    lime: 'bg-lime-50 border-lime-200 text-forest',
    forest: 'bg-forest-50 border-forest-100 text-forest',
    muted: 'bg-white border-forest/10 text-forest'
  }[tone];
  return <div className={`rounded-xl p-3 border ${styles}`} data-id="element-186">
      <p className="text-[10px] uppercase tracking-wider font-bold text-forest/60" data-id="element-187">
        {label}
      </p>
      <p className="text-base font-extrabold mt-1 tracking-tight" data-id="element-188">{value}</p>
    </div>;
}